import { supabase } from '../lib/supabase';
import { Order } from './shopService';
import { getDomainFilterParams } from '../hooks/useDomainQuery';

// Récupérer la configuration du tenant avec résolution par domaine
export const getTenantConfig = async () => {
  try {
    // Résolution dynamique du tenant par domaine en premier
    const currentDomain = window.location.hostname;
    console.log('🌐 shopEmailService getTenantConfig - Résolution par domaine:', currentDomain);
    
    // Tentative de résolution par domaine
    const { data: resolvedData, error: resolveError } = await supabase
      .rpc('resolve_tenant_by_domain', { domain_input: currentDomain });

    if (!resolveError && resolvedData && resolvedData.length > 0) {
      const resolved = resolvedData[0];
      console.log('✅ shopEmailService getTenantConfig - Tenant résolu:', resolved.tenant_id, resolved.tenant_name);
      
      // Utiliser la configuration fusionnée avec email du domaine
      return {
        email: resolved.merged_config?.email || `contact@${currentDomain}`,
        name: resolved.merged_config?.name || resolved.tenant_name,
        logo: resolved.merged_config?.logo || '',
        domain_name: currentDomain,
        config: resolved.merged_config || {},
        ...resolved.merged_config // Pour les configurations supplémentaires
      };
    }
    
    // Fallback vers getDomainFilterParams si la résolution par domaine échoue
    console.log('⚠️ shopEmailService getTenantConfig - Résolution par domaine échouée, fallback vers getDomainFilterParams');
    const { tenantId } = getDomainFilterParams();
    
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('id', tenantId)
      .single();

    if (error) {
      console.error('Erreur lors de la récupération de la configuration du tenant:', error);
      // Retourner des valeurs par défaut en cas d'erreur
      return {
        email: 'contact@example.com',
        name: 'Default Tenant',
        logo: '',
        domain_name: 'example.com',
        config: {}
      };
    }

    return {
      email: data.email || `contact@${data.domain_name || 'example.com'}`,
      name: data.name || 'Tenant',
      logo: data.logo || '',
      domain_name: data.domain_name || 'example.com',
      config: data.config || {},
      ...data.config // Pour les configurations supplémentaires
    };
  } catch (err) {
    console.error('Erreur inattendue dans shopEmailService getTenantConfig:', err);
    return {
      email: 'contact@example.com',
      name: 'Default Tenant',
      logo: '',
      domain_name: 'example.com',
      config: {}
    };
  }
};

// Type pour les informations d'expédition
interface SenderInfo {
  email: string;
  name: string;
}

// Type pour les expéditeurs
interface EmailSenders {
  default: SenderInfo;
  tickets: SenderInfo;
  shop: SenderInfo;
  support: SenderInfo;
  [key: string]: SenderInfo;
}

// Configuration des expéditeurs
const getEmailSenders = async (): Promise<EmailSenders> => {
  const tenant = await getTenantConfig();
  
  return {
    default: {
      email: tenant.email,
      name: tenant.name
    },
    tickets: {
      email: `billetterie@${tenant.domain_name}`,
      name: `${tenant.name} Billeterie`
    },
    shop: {
      email: tenant.config?.email2 || tenant.email,
      name: `${tenant.name} Boutique`
    },
    support: {
      email: `support@${tenant.domain_name}`,
      name: `Support ${tenant.name}`
    }
  };
};

// Fonction utilitaire pour obtenir les informations de l'expéditeur
export const getSenderInfo = async (senderType?: string): Promise<SenderInfo> => {
  const senders = await getEmailSenders();
  if (!senderType || !(senderType in senders)) {
    return senders.default;
  }
  return senders[senderType];
};

interface OtpEmailData {
  to: string;
  name: string;
  otpCode: string;
}

interface OrderConfirmationEmailData {
  to: string;
  name: string;
  orderCode: string;
  orderDate: string;
  orderItems: Array<{
    title: string;
    quantity: number;
    price: number;
    image_url?: string;
  }>;
  totalAmount: number;
  trackingCode: string;
}

/**
 * Envoie un code OTP par email
 */
const sendOtpEmail = async (data: OtpEmailData): Promise<{ success: boolean; error?: string }> => {
  try {
    const session = await supabase.auth.getSession();
    const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
    
    const tenant = await getTenantConfig();
    
    // Préparer le contenu texte
    const textContent = `
Bonjour ${data.name},

Voici votre code de vérification : ${data.otpCode}

Ce code est valable pendant 10 minutes.

Cordialement,
L'équipe de ${tenant.name}
`;

    // Préparer le contenu HTML
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background-color: #000; padding: 20px; text-align: center;">
          <img src="${tenant.logo}" alt="${tenant.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
          <h2 style="margin: 0; color: #fff;">Votre code de vérification</h2>
        </div>
        <div style="padding: 20px; background-color: #fff; color: #333;">
          <p>Bonjour ${data.name},</p>
          <p>Voici votre code de vérification :</p>
          
          <div style="background-color: #f9f9f9; border: 1px solid #eee; padding: 20px; margin: 20px 0; text-align: center;">
            <h1 style="font-size: 32px; letter-spacing: 5px; margin: 0;">${data.otpCode}</h1>
          </div>
          
          <p>Ce code est valable pendant <strong>10 minutes</strong>.</p>
          <p>Si vous n'avez pas demandé ce code, vous pouvez ignorer cet email.</p>
          
          <p>Cordialement,<br>L'équipe de ${tenant.name}</p>
        </div>
        <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
          <p>&copy; ${new Date().getFullYear()} ${tenant.name}. Tous droits réservés.</p>
          <p>Ceci est un email automatique, merci de ne pas y répondre directement.</p>
        </div>
      </div>
    `;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.data?.session?.access_token}`
      },
      body: JSON.stringify({
        to: data.to,
        toName: data.name,
        subject: 'Votre code de vérification',
        htmlContent,
        textContent,
        fromEmail: tenant.email,
        fromName: tenant.name,
        messageId: 0
      })
    });

    const result = await response.json();
    console.log('Réponse de la fonction Edge:', result);

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
    }

    return { success: true };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi de l\'email OTP:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'envoi de l\'email OTP' };
  }
};

interface OrderEmailData {
  to: string;
  name: string;
  orderCode: string;
  orderDate: string;
  orderItems: {
    title: string;
    price: number;
    quantity: number;
    imageUrl?: string;
  }[];
  totalAmount: number;
  shippingAddress?: {
    address: string;
    city: string;
    country: string;
    postalCode: string;
  };
  trackingCode?: string;
}

interface ShopOtpEmailData {
  to: string;
  name: string;
  otpCode: string;
  purpose?: 'login' | 'order_access' | 'profile_update';
}

/**
 * Envoie un email de confirmation de commande avec les données fournies
 * @deprecated Utiliser la nouvelle version qui prend directement l'ID de commande
 */
export const sendOrderConfirmationEmailWithData = async (data: OrderEmailData): Promise<{ success: boolean; error?: string }> => {
  try {
    const session = await supabase.auth.getSession();
    const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
    
    const tenant = await getTenantConfig();
    const sender = await getSenderInfo('shop');
    
    // Générer le HTML pour les articles de la commande
    const itemsHtml = data.orderItems.map(item => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">
          <div style="display: flex; align-items: center;">
            ${item.imageUrl ? `<img src="${item.imageUrl}" alt="${item.title}" style="width: 50px; height: 50px; object-fit: cover; margin-right: 10px;">` : ''}
            <span>${item.title}</span>
          </div>
        </td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${new Intl.NumberFormat('fr-FR').format(item.price)} FCFA</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${new Intl.NumberFormat('fr-FR').format(item.price * item.quantity)} FCFA</td>
      </tr>
    `).join('');
    
    // Préparer le contenu texte
    const textContent = `
Bonjour ${data.name},

Merci pour votre commande ! Votre commande #${data.orderCode} a été confirmée.

Détails de la commande :
${data.orderItems.map(item => `- ${item.title} (${item.quantity}x) : ${new Intl.NumberFormat('fr-FR').format(item.price * item.quantity)} FCFA`).join('\n')}

Total : ${new Intl.NumberFormat('fr-FR').format(data.totalAmount)} FCFA

${data.shippingAddress ? `
Adresse de livraison :
${data.shippingAddress.address}
${data.shippingAddress.city}
${data.shippingAddress.country}
${data.shippingAddress.postalCode}
` : ''}

${data.trackingCode ? `Code de suivi : ${data.trackingCode}` : ''}

Pour accéder à votre commande et suivre son statut, visitez : https://${tenant.domain_name}/boutique/commandes?code=${data.orderCode}

Cordialement,
L'équipe de ${tenant.name}
`;

    // Préparer le contenu HTML
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background-color: #000; padding: 20px; text-align: center;">
          <img src="${tenant.logo}" alt="${tenant.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
          <h2 style="margin: 0; color: #fff;">Confirmation de commande</h2>
        </div>
        <div style="padding: 20px; background-color: #fff; color: #333;">
          <p>Bonjour ${data.name},</p>
          <p>Merci pour votre commande ! Votre commande <strong>#${data.orderCode}</strong> du <strong>${data.orderDate}</strong> a été confirmée.</p>
          
          <div style="background-color: #f9f9f9; border-left: 4px solid #000; padding: 15px; margin: 15px 0;">
            <h3 style="margin-top: 0;">Détails de la commande</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #eee;">
                  <th style="padding: 10px; text-align: left;">Produit</th>
                  <th style="padding: 10px; text-align: center;">Qté</th>
                  <th style="padding: 10px; text-align: right;">Prix unitaire</th>
                  <th style="padding: 10px; text-align: right;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" style="padding: 10px; text-align: right; font-weight: bold;">Total</td>
                  <td style="padding: 10px; text-align: right; font-weight: bold;">${new Intl.NumberFormat('fr-FR').format(data.totalAmount)} FCFA</td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          ${data.shippingAddress ? `
          <div style="background-color: #f9f9f9; border-left: 4px solid #000; padding: 15px; margin: 15px 0;">
            <h3 style="margin-top: 0;">Adresse de livraison</h3>
            <p>${data.shippingAddress.address}</p>
            <p>${data.shippingAddress.city}, ${data.shippingAddress.country}, ${data.shippingAddress.postalCode}</p>
          </div>
          ` : ''}
          
          ${data.trackingCode ? `
          <div style="background-color: #f9f9f9; border-left: 4px solid #000; padding: 15px; margin: 15px 0;">
            <h3 style="margin-top: 0;">Suivi de commande</h3>
            <p><strong>Code de suivi :</strong> ${data.trackingCode}</p>
          </div>
          ` : ''}
          
          <div style="text-align: center; margin-top: 20px; margin-bottom: 20px;">
            <a href="https://${tenant.domain_name}/boutique/commandes?code=${data.orderCode}" style="display: inline-block; background-color: #333; color: #fff; text-decoration: none; padding: 12px 20px; border-radius: 4px; font-weight: bold;">Suivre ma commande</a>
          </div>
          
          <p>Cordialement,<br>L'équipe de ${tenant.name}</p>
        </div>
        <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
          <p>&copy; ${new Date().getFullYear()} ${tenant.name}. Tous droits réservés.</p>
          <p>Ceci est un email automatique, merci de ne pas y répondre directement.</p>
        </div>
      </div>
    `;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.data?.session?.access_token}`
      },
      body: JSON.stringify({
        to: data.to,
        toName: data.name,
        subject: `Confirmation de votre commande #${data.orderCode}`,
        htmlContent,
        textContent,
        fromEmail: sender.email,
        fromName: sender.name,
        messageId: 0
      })
    });

    const result = await response.json();
    console.log('Réponse de la fonction Edge:', result);

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
    }

    return { success: true };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi de l\'email de confirmation de commande:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'envoi de l\'email de confirmation' };
  }
};

/**
 * Envoie un email avec un code OTP pour accéder aux commandes ou au profil
 */
export const sendOrderConfirmationEmail = async (orderId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('[shopEmailService] Début de l\'envoi d\'email de confirmation pour la commande:', orderId);
    
    // Récupérer les détails de la commande et les éléments en parallèle pour accélérer
    const [orderResponse, itemsResponse] = await Promise.all([
      supabase.from('shop_orders').select('*').eq('id', orderId).single(),
      supabase.from('shop_order_items').select(`
        *,
        product:product_id(title, price, images:shop_product_images(image_url))
      `).eq('order_id', orderId)
    ]);
    
    const { data: order, error: orderError } = orderResponse;
    const { data: orderItems, error: itemsError } = itemsResponse;
      
    // Vérifier les erreurs
    if (orderError || !order) {
      console.error('[shopEmailService] Erreur lors de la récupération de la commande:', orderError?.message || 'Commande introuvable');
      throw new Error(`Erreur lors de la récupération de la commande: ${orderError?.message || 'Commande introuvable'}`);
    }
    
    if (itemsError) {
      console.error('[shopEmailService] Erreur lors de la récupération des éléments de la commande:', itemsError.message);
      throw new Error(`Erreur lors de la récupération des éléments de la commande: ${itemsError.message}`);
    }
    
    // Forcer le statut à 'paid' puisque cette fonction est appelée après confirmation du paiement
    order.status = 'paid';
    
    console.log('[shopEmailService] Détails de la commande et éléments récupérés:', orderItems?.length || 0, 'produits');

    // Récupérer les informations du tenant et du sender en parallèle
    const [tenant, sender] = await Promise.all([
      getTenantConfig(),
      getSenderInfo('shop')
    ]);

    // Préparer les données des produits pour l'email
    const formattedItems = (orderItems || []).map((item: any) => ({
      title: item.product?.title || 'Produit',
      quantity: item.quantity,
      price: item.price_at_purchase,
      image_url: item.product?.images?.[0]?.image_url || ''
    }));
    
    console.log('[shopEmailService] Produits formatés pour l\'email:', formattedItems.length);

    // Formater la date de commande
    const orderDate = new Date(order.created_at).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Préparer le contenu HTML pour les produits
    const productsHtml = formattedItems.map((item: any) => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">
          ${item.image_url ? `<img src="${item.image_url}" alt="${item.title}" style="width: 50px; height: 50px; object-fit: cover;">` : ''}
        </td>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.title}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${item.price.toLocaleString('fr-FR')} FCFA</td>
      </tr>
    `).join('');

    // Préparer le contenu texte
    const textContent = `
Bonjour ${order.customer_name},

Nous vous remercions pour votre commande sur ${tenant.name}.

Détails de la commande #${order.order_number} (${orderDate}) :

${formattedItems.map((item: any) => `- ${item.quantity}x ${item.title} : ${item.price.toLocaleString('fr-FR')} FCFA`).join('\n')}

Total : ${order.total_amount.toLocaleString('fr-FR')} FCFA

Statut : ${order.status === 'paid' ? 'Payée' : 'En attente de paiement'}

Vous pouvez suivre votre commande à tout moment en vous connectant à votre espace client.

Cordialement,
L'équipe de ${tenant.name}
`;

    // Préparer le contenu HTML
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background-color: #000; padding: 20px; text-align: center;">
          <img src="${tenant.logo}" alt="${tenant.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
          <h2 style="margin: 0; color: #fff;">Confirmation de commande</h2>
        </div>
        <div style="padding: 20px; background-color: #fff; color: #333;">
          <p>Bonjour ${order.customer_name},</p>
          <p>Nous vous remercions pour votre commande sur ${tenant.name}.</p>
          
          <div style="background-color: #f9f9f9; border-left: 4px solid #000; padding: 15px; margin: 15px 0;">
            <h3 style="margin-top: 0;">Détails de la commande #${order.order_number}</h3>
            <p><strong>Date :</strong> ${orderDate}</p>
            <p><strong>Statut :</strong> ${order.status === 'paid' ? '<span style="color: green;">Payée</span>' : '<span style="color: orange;">En attente de paiement</span>'}</p>
          </div>
          
          <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <thead>
              <tr style="background-color: #f2f2f2;">
                <th style="padding: 10px; text-align: left;">Image</th>
                <th style="padding: 10px; text-align: left;">Produit</th>
                <th style="padding: 10px; text-align: center;">Qté</th>
                <th style="padding: 10px; text-align: right;">Prix</th>
              </tr>
            </thead>
            <tbody>
              ${productsHtml}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" style="padding: 10px; text-align: right; font-weight: bold;">Total :</td>
                <td style="padding: 10px; text-align: right; font-weight: bold;">${order.total_amount.toLocaleString('fr-FR')} FCFA</td>
              </tr>
            </tfoot>
          </table>
          
          <div style="margin-top: 30px;">
            <p>Vous pouvez suivre votre commande à tout moment en vous connectant à votre espace client.</p>
            <div style="text-align: center; margin-top: 20px; margin-bottom: 20px;">
              <a href="https://${tenant.domain_name}/boutique/commandes" style="display: inline-block; background-color: #333; color: #fff; text-decoration: none; padding: 12px 20px; border-radius: 4px; font-weight: bold;">Voir mes commandes</a>
            </div>
          </div>
          
          <p>Cordialement,<br>L'équipe de ${tenant.name}</p>
        </div>
        <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
          <p>&copy; ${new Date().getFullYear()} ${tenant.name}. Tous droits réservés.</p>
          <p>Ceci est un email automatique, merci de ne pas y répondre directement.</p>
        </div>
      </div>
    `;

    // Envoyer l'email
    const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
    
    // Utiliser la clé d'API Supabase pour l'authentification
    const { VITE_SUPABASE_KEY } = import.meta.env;

    console.log('[shopEmailService] Préparation de l\'envoi de l\'email...');
    console.log('[shopEmailService] Destinataire:', order.customer_email);
    console.log('[shopEmailService] Sujet:', `Confirmation de votre commande #${order.order_number}`);
    console.log('[shopEmailService] Expéditeur:', sender.email, sender.name);
    
    // Valider les données avant envoi
    if (!order.customer_email || !order.customer_name || !sender.email || !sender.name) {
      throw new Error('Données d\'email manquantes: ' + JSON.stringify({
        customer_email: !!order.customer_email,
        customer_name: !!order.customer_name,
        sender_email: !!sender.email,
        sender_name: !!sender.name
      }));
    }
    
    try {
      const payload = {
        to: order.customer_email,
        toName: order.customer_name,
        subject: `Confirmation de votre commande #${order.order_number}`,
        htmlContent,
        textContent,
        fromEmail: sender.email,
        fromName: sender.name,
        messageId: 0
      };
      
      console.log('[shopEmailService] Payload envoyé:', JSON.stringify(payload, null, 2));
      
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${VITE_SUPABASE_KEY}`
        },
        body: JSON.stringify(payload)
      });

      console.log('[shopEmailService] Réponse de la requête:', response.status, response.statusText);
      
      const result = await response.json();
      console.log('[shopEmailService] Réponse de la fonction Edge pour l\'email de confirmation:', result);

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
      }
    } catch (fetchError: any) {
      console.error('[shopEmailService] Erreur lors de la requête fetch:', fetchError.message);
      throw fetchError;
    }

    return { success: true };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi de l\'email de confirmation de commande:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'envoi de l\'email de confirmation' };
  }
};

/**
 * Envoie un email avec un code OTP pour accéder aux commandes ou au profil
 */
export const sendShopOtpEmail = async (data: ShopOtpEmailData): Promise<{ success: boolean; error?: string }> => {
  try {
    const session = await supabase.auth.getSession();
    const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
    
    const tenant = await getTenantConfig();
    const sender = await getSenderInfo('shop');
    
    // Déterminer le sujet et le message en fonction du but
    let subject = 'Votre code de vérification';
    let purposeText = 'accéder à votre compte';
    
    if (data.purpose === 'order_access') {
      subject = 'Code d\'accès à vos commandes';
      purposeText = 'accéder à vos commandes';
    } else if (data.purpose === 'profile_update') {
      subject = 'Code de vérification pour mise à jour de profil';
      purposeText = 'mettre à jour votre profil';
    }
    
    // Préparer le contenu texte
    const textContent = `
Bonjour ${data.name},

Voici votre code de vérification pour ${purposeText} : ${data.otpCode}

Ce code est valable pendant 10 minutes.

Cordialement,
L'équipe de ${tenant.name}
`;

    // Préparer le contenu HTML
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background-color: #000; padding: 20px; text-align: center;">
          <img src="${tenant.logo}" alt="${tenant.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
          <h2 style="margin: 0; color: #fff;">Votre code de vérification</h2>
        </div>
        <div style="padding: 20px; background-color: #fff; color: #333;">
          <p>Bonjour ${data.name},</p>
          <p>Voici votre code de vérification pour ${purposeText} :</p>
          
          <div style="background-color: #f9f9f9; border: 1px solid #eee; padding: 20px; margin: 20px 0; text-align: center;">
            <h1 style="font-size: 32px; letter-spacing: 5px; margin: 0;">${data.otpCode}</h1>
          </div>
          
          <p>Ce code est valable pendant <strong>10 minutes</strong>.</p>
          <p>Si vous n'avez pas demandé ce code, vous pouvez ignorer cet email.</p>
          
          <p>Cordialement,<br>L'équipe de ${tenant.name}</p>
        </div>
        <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
          <p>&copy; ${new Date().getFullYear()} ${tenant.name}. Tous droits réservés.</p>
          <p>Ceci est un email automatique, merci de ne pas y répondre directement.</p>
        </div>
      </div>
    `;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.data?.session?.access_token}`
      },
      body: JSON.stringify({
        to: data.to,
        toName: data.name,
        subject,
        htmlContent,
        textContent,
        fromEmail: sender.email,
        fromName: sender.name,
        messageId: 0
      })
    });

    const result = await response.json();
    console.log('Réponse de la fonction Edge:', result);

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
    }

    return { success: true };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi de l\'email OTP:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'envoi de l\'email OTP' };
  }
};
